import { z } from 'zod';
import {
  type CommitOnboardingTemplateImportRequest,
  type CreateOnboardingTemplateFromDescriptionRequest,
  type CreateOnboardingTemplateRequest,
  type ReplaceOnboardingTemplateDraftFromDescriptionRequest,
  type OnboardingTemplateListQuery,
  type UpdateOnboardingTemplateRequest,
  onboardingTemplateDetailSchema,
  onboardingTemplateExchangeSchema,
  onboardingTemplateImportPreviewSchema,
  onboardingTemplateListResponseSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const onboardingTemplatesKey = (query: OnboardingTemplateListQuery) =>
  ['onboarding', 'templates', query] as const;

export const onboardingTemplateKey = (id: string) => ['onboarding', 'template', id] as const;

export function useOnboardingTemplates(
  query: OnboardingTemplateListQuery,
  options?: { enabled?: boolean },
) {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize));
  search.set('sortBy', query.sortBy);
  search.set('sortOrder', query.sortOrder);
  if (query.q) {
    search.set('q', query.q);
  }
  if (query.kind) {
    search.set('kind', query.kind);
  }
  if (query.isActive !== undefined) {
    search.set('isActive', String(query.isActive));
  }
  return useQuery({
    queryKey: onboardingTemplatesKey(query),
    queryFn: () =>
      apiFetch(`/onboarding/templates?${search.toString()}`, onboardingTemplateListResponseSchema),
    enabled: options?.enabled ?? true,
  });
}

export function useOnboardingTemplate(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: onboardingTemplateKey(id),
    queryFn: () => apiFetch(`/onboarding/templates/${id}`, onboardingTemplateDetailSchema),
    enabled: (options?.enabled ?? true) && Boolean(id),
  });
}

export function useCreateOnboardingTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOnboardingTemplateRequest) =>
      apiFetch('/onboarding/templates', onboardingTemplateDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(data.id), data);
    },
  });
}

export function useCreateOnboardingTemplateFromDescription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOnboardingTemplateFromDescriptionRequest) =>
      apiFetch('/onboarding/templates/from-description', onboardingTemplateDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(data.id), data);
    },
  });
}

export function useReplaceOnboardingTemplateDraft(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ReplaceOnboardingTemplateDraftFromDescriptionRequest) =>
      apiFetch(
        `/onboarding/templates/${id}/draft-from-description`,
        onboardingTemplateDetailSchema,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(id), data);
    },
  });
}

export function useExportOnboardingTemplate() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/onboarding/templates/${id}/export`, onboardingTemplateExchangeSchema),
  });
}

export function usePreviewOnboardingTemplateImport() {
  return useMutation({
    mutationFn: (document: unknown) =>
      apiFetch('/onboarding/templates/import/preview', onboardingTemplateImportPreviewSchema, {
        method: 'POST',
        body: JSON.stringify({ document }),
      }),
  });
}

export function useCommitOnboardingTemplateImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CommitOnboardingTemplateImportRequest) =>
      apiFetch('/onboarding/templates/import', onboardingTemplateDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(data.id), data);
    },
  });
}

export function useUpdateOnboardingTemplate(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOnboardingTemplateRequest) =>
      apiFetch(`/onboarding/templates/${id}`, onboardingTemplateDetailSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(id), data);
    },
  });
}

export function usePublishOnboardingTemplate(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/onboarding/templates/${id}/publish`, onboardingTemplateDetailSchema, {
        method: 'POST',
      }),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(id), data);
    },
  });
}

const onboardingRemovedSchema = z.object({ id: z.string().uuid() });

export function useDeleteOnboardingTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/onboarding/templates/${id}`, onboardingRemovedSchema, { method: 'DELETE' }),
    onSuccess: async (_data, id) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.removeQueries({ queryKey: onboardingTemplateKey(id) });
    },
  });
}

export function useRetireOnboardingTemplate(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/onboarding/templates/${id}/retire`, onboardingTemplateDetailSchema, {
        method: 'POST',
      }),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
      client.setQueryData(onboardingTemplateKey(id), data);
    },
  });
}
